import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { listBills, listSales, listPartners, changeBillPartner, opsErr, okCode } from '../../api/ops';
import { useToast } from '../../components/Toast';
import { useModal } from '../../components/ModalProvider';
import { OpsGate, ScanSearch, daysAgo, localIso, fmtDateTime } from './opsCommon';
import { playOpsSound } from './scanner';

// NHẶT HÀNG (app "i love GTS" -> Kiểm soát Phiếu xuất kho + Nhặt hàng):
// 1) Chọn phiếu xuất kho (status 15) theo sale / đối tác -> 2) quét tracking từng kiện -> đủ hết mới "Lưu lại"
//    (POST /api/bill/change/partner, is_collect = true).

function SalesFilter({ sales, selected, onApply, onClose }) {
  const [sel, setSel] = useState(() => new Set(selected));
  const all = sel.size === sales.length;
  const toggle = (id) => setSel((p) => { const n = new Set(p); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  return (
    <div>
      <h3>Lọc nhân viên sale</h3>
      <label className="checkrow">
        <input type="checkbox" checked={all} onChange={() => setSel(all ? new Set() : new Set(sales.map((s) => s._id)))} />
        <div className="meta"><div className="nm">Chọn tất cả ({sales.length})</div></div>
      </label>
      <div style={{ maxHeight: '50vh', overflow: 'auto' }}>
        {sales.map((s) => (
          <label key={s._id} className="checkrow">
            <input type="checkbox" checked={sel.has(s._id)} onChange={() => toggle(s._id)} />
            <div className="meta">
              <div className="nm">{s.staff_code} - {s.full_name}</div>
              <div className="dt">{s.station?.role?.name || s.station?.role_code}</div>
            </div>
          </label>
        ))}
      </div>
      <div className="row" style={{ justifyContent: 'flex-end', marginTop: 12 }}>
        <button className="btn ghost" onClick={onClose}>Hủy</button>
        <button className="btn" disabled={!sel.size} onClick={() => onApply([...sel])}>Lọc nhân viên</button>
      </div>
    </div>
  );
}

// Bước 2: quét tracking từng sản phẩm của các phiếu đã chọn.
function Pickup({ bills, onBack, onSaved }) {
  const toast = useToast();
  const { confirm } = useModal();
  const products = useMemo(
    () => bills.flatMap((b) => (b.products || []).map((p, i) => ({ ...p, _key: `${b._id}-${i}` }))),
    [bills]
  );
  const [picked, setPicked] = useState(() => new Set());
  const [q, setQ] = useState('');
  const [saving, setSaving] = useState(false);
  const allPicked = products.length > 0 && picked.size === products.length;

  const onScan = (code) => {
    const c = String(code || '').trim();
    if (!c) return;
    const hits = products.filter((p) => String(p.tracking_no || '').trim() === c);
    setQ('');
    if (!hits.length) { playOpsSound('no'); return toast(`Không có mã ${c} trong phiếu đã chọn`, 'danger'); }
    if (hits.every((p) => picked.has(p._key))) { playOpsSound('exist'); return toast(`Mã ${c} đã được nhặt rồi`, 'warn'); }
    playOpsSound('yes');
    setPicked((prev) => { const n = new Set(prev); hits.forEach((p) => n.add(p._key)); return n; });
    toast(`Đã nhặt ${c}`);
  };

  const reset = async () => {
    if (await confirm('Bỏ chọn toàn bộ các mã đã nhặt?', { okText: 'Bỏ chọn' })) setPicked(new Set());
  };

  const save = async () => {
    setSaving(true);
    try {
      const d = await changeBillPartner({
        partner_code: bills[0].partner_code,
        partner_name: bills[0].partner_name,
        ids: bills.map((b) => b._id),
        is_collect: true,
      });
      if (okCode(d)) { toast('Cập nhật thành công'); onSaved(); }
      else toast(d?.message || 'Cập nhật thất bại', 'danger');
    } catch (e) {
      toast(opsErr(e, 'Cập nhật thất bại'), 'danger');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="row between" style={{ marginBottom: 10 }}>
        <button className="btn sm ghost" onClick={onBack}><i className="ti ti-arrow-left" /> Chọn phiếu</button>
        <div className="row">
          <span className="small">Đã nhặt <b>{picked.size}</b>/{products.length}</span>
          <button className="btn sm ghost" onClick={reset} title="Bỏ chọn tất cả"><i className="ti ti-refresh" /></button>
        </div>
      </div>
      <ScanSearch value={q} onChange={setQ} onSubmit={onScan} placeholder="Quét / nhập mã tracking…" autoFocus />
      <div className="ops-list">
        {products.map((p) => (
          <div key={p._key} className={`card ops-item${picked.has(p._key) ? ' ops-picked' : ''}`}>
            <div className="row between">
              <b>{p.tracking_no}</b>
              <span className="row" style={{ gap: 6 }}>
                {p.weight != null && <span className="small muted">{p.weight} kg</span>}
                {p.position && <span className="ops-pos">{String(p.position).toUpperCase()}</span>}
                {picked.has(p._key) && <i className="ti ti-circle-check-filled" style={{ color: 'var(--accent2)' }} />}
              </span>
            </div>
            {p.link ? <a className="small" href={p.link} target="_blank" rel="noreferrer">{p.name || p.link}</a> : <div className="small muted">{p.name}</div>}
          </div>
        ))}
        {!products.length && <div className="empty">Phiếu đã chọn không có sản phẩm.</div>}
      </div>
      <div className="ops-footer">
        <button className="btn" style={{ width: '100%' }} disabled={!allPicked || saving} onClick={save}>
          {saving ? 'Đang lưu…' : allPicked ? 'Lưu lại' : `Còn ${products.length - picked.size} mã chưa nhặt`}
        </button>
      </div>
    </>
  );
}

function PickList() {
  const toast = useToast();
  const { openModal, closeModal } = useModal();
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [sales, setSales] = useState([]);
  const [saleIds, setSaleIds] = useState(null); // null = tất cả
  const [partners, setPartners] = useState([]);
  const [partner, setPartner] = useState('');
  const [sel, setSel] = useState(() => new Set());
  const [picking, setPicking] = useState(null); // phiếu đang nhặt
  const qRef = useRef('');
  qRef.current = q;

  const load = useCallback(async (search = qRef.current) => {
    setLoading(true);
    try {
      const params = { from: localIso(daysAgo(360)), to: localIso(new Date()), page: 1, size: 100, status: 15 };
      if (search) params.search = search;
      if (partner) params.partner = partner;
      if (saleIds && saleIds.length !== sales.length) params.staffs = saleIds;
      const d = await listBills(params);
      if (okCode(d, 0)) setBills(d.data?.results || []);
      else { setBills([]); toast(d?.message || 'Không tải được phiếu xuất kho', 'danger'); }
    } catch (e) {
      setBills([]);
      toast(opsErr(e, 'Không tải được phiếu xuất kho'), 'danger');
    } finally {
      setSel(new Set());
      setLoading(false);
    }
  }, [partner, saleIds, sales.length, toast]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    listSales().then((d) => { if (okCode(d)) setSales((d.data || []).filter((s) => String(s.station?.role_code || '').toLowerCase() === 'ss')); }).catch(() => {});
    listPartners().then((d) => { if (okCode(d, 0)) setPartners(d.data?.results || []); }).catch(() => {});
  }, []);

  if (picking) return <Pickup bills={picking} onBack={() => setPicking(null)} onSaved={() => { setPicking(null); load(); }} />;

  const allSel = bills.length > 0 && sel.size === bills.length;
  const toggle = (id) => setSel((p) => { const n = new Set(p); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const nSales = saleIds ? saleIds.length : sales.length;

  return (
    <>
      <ScanSearch value={q} onChange={setQ} onSubmit={(c) => load(c)} placeholder="Tìm phiếu / mã…" busy={loading} />
      <div className="row" style={{ marginBottom: 10 }}>
        <button className="btn sm ghost" disabled={!sales.length} onClick={() => openModal(
          <SalesFilter sales={sales} selected={saleIds || sales.map((s) => s._id)} onClose={closeModal} onApply={(ids) => { setSaleIds(ids); closeModal(); }} />
        )}>
          <i className="ti ti-users" /> ({nSales}) nhân viên
        </button>
        <select value={partner} onChange={(e) => setPartner(e.target.value)} style={{ width: 'auto', minWidth: 140 }}>
          <option value="">Tất cả đối tác</option>
          {partners.map((p) => <option key={p._id || p.code} value={p.code}>{p.name}</option>)}
        </select>
        <label className="small row" style={{ gap: 6, marginLeft: 'auto' }}>
          <input type="checkbox" checked={allSel} onChange={() => setSel(allSel ? new Set() : new Set(bills.map((b) => b._id)))} /> Chọn tất cả
        </label>
      </div>
      {loading ? <div className="empty">Đang tải…</div> : !bills.length ? <div className="empty">Không có phiếu xuất kho.</div> : (
        <div className="ops-list">
          {bills.map((b) => (
            <label key={b._id} className={`card ops-item ops-check${b.is_collect ? ' ops-collected' : ''}`}>
              <input type="checkbox" checked={sel.has(b._id)} onChange={() => toggle(b._id)} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="row between"><b>{b.code}</b><span className="small muted">{fmtDateTime(b.status_date)}</span></div>
                <div className="small">{b.user?.customer_no} - {b.user?.name || b.user?.full_name}</div>
                <div className="small muted">
                  {b.partner_code}{b.weight != null ? ` · ${b.weight} kg` : ''}{b.description ? ` · ${b.description}` : ''}
                </div>
              </div>
            </label>
          ))}
        </div>
      )}
      <div className="ops-footer">
        <button className="btn" style={{ width: '100%' }} disabled={!sel.size} onClick={() => setPicking(bills.filter((b) => sel.has(b._id)))}>
          Kiểm soát ({sel.size})
        </button>
      </div>
    </>
  );
}

export default function OpsPickPage({ onBack }) {
  return (
    <OpsGate onBack={onBack} title="Nhặt hàng" sub="Kiểm soát phiếu xuất kho — quét đủ mã tracking rồi lưu">
      <PickList />
    </OpsGate>
  );
}
